import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { pendenciasDeRetornoDeHoje, excluirAvisoDeRetorno } from '../../visitas/logica'
import type { Retorno } from '../../visitas/retorno'
import { confirmar } from '../../componentes/confirmar'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import { dataISOLocal } from '../../pedidos/precos'
import { useAgora } from '../../lib/useAgora'
import {
  semanasDoMes, corDoDia, compromissosDoDiaComExcluidos, ehAtrasado, nomeDoCliente,
} from '../../compromissos/regras'

// Spec 2026-10-02 (ciclo 2A), item 2: calendario do mes com bolinha por dia e,
// embaixo, a lista do dia escolhido. Abre no mes de hoje com hoje selecionado
// (ou no ?data= que o formulario devolve depois de salvar).
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
const DIAS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']
const DIAS_EXTENSO = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
const BOLINHA = { vermelha: 'bg-red-500', verde: 'bg-emerald-500', cinza: 'bg-slate-400' }

export default function MeusCompromissos() {
  const [parametros] = useSearchParams()
  const agora = useAgora()
  const inicial = parametros.get('data') ?? dataISOLocal(agora)
  const [selecionado, setSelecionado] = useState(inicial)
  const [mes, setMes] = useState(() => {
    const [a, m] = inicial.split('-').map(Number)
    return { ano: a, mes: m - 1 }
  })

  const semanas = semanasDoMes(mes.ano, mes.mes)
  const primeiro = semanas.flat().find(Boolean)!
  const ultimo = [...semanas.flat()].reverse().find(Boolean)!
  const doMes = useLiveQuery(
    () => banco.compromissos.where('data').between(primeiro, ultimo, true, true).toArray(),
    [primeiro, ultimo],
  ) ?? []
  const doSelecionado = useLiveQuery(
    () => banco.compromissos.where('data').equals(selecionado).toArray(),
    [selecionado],
  ) ?? []
  const clientes = useLiveQuery(() => banco.clientes.toArray(), []) ?? []
  // Ciclo 2B: retorno vencido vira pendencia de HOJE (calculada, nao gravada).
  const hojeISO = dataISOLocal(agora)
  const navegar = useNavigate()
  const retornos = useLiveQuery(() => pendenciasDeRetornoDeHoje(new Date()), [hojeISO]) ?? []
  const retornoAtrasado = retornos.some((r) => r.diasDeAtraso > 0)
  async function excluirRetorno(r: Retorno) {
    if (!(await confirmar('Excluir este aviso? O cliente continua na rota até ser visitado.', { botao: 'Excluir', perigo: true }))) return
    await excluirAvisoDeRetorno(r.cliente.id, r.vencimento)
  }

  const trocarMes = (passo: number) => {
    const d = new Date(mes.ano, mes.mes + passo, 1)
    setMes({ ano: d.getFullYear(), mes: d.getMonth() })
  }
  const [a, m, d] = selecionado.split('-').map(Number)
  // Teste dela 04/10, item 7: o excluido fica cinza, riscado, sem abrir.
  const lista = compromissosDoDiaComExcluidos(doSelecionado, selecionado)

  return (
    <section className="p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-bold text-slate-800">Meus compromissos</h2>
        <Link to={`/compromissos/novo?data=${selecionado}`} data-testid="novo-compromisso"
          className="rounded-lg bg-teal-700 text-white px-3 py-2 text-sm font-semibold">
          + Compromisso
        </Link>
      </div>

      <div data-testid="calendario" className="bg-white rounded-xl shadow-sm p-3">
        <div className="flex items-center justify-between mb-2">
          <button type="button" onClick={() => trocarMes(-1)} aria-label="Mês anterior" className="px-3 text-xl text-slate-600">‹</button>
          <p className="font-semibold text-slate-800">{MESES[mes.mes]} {mes.ano}</p>
          <button type="button" onClick={() => trocarMes(1)} aria-label="Próximo mês" className="px-3 text-xl text-slate-600">›</button>
        </div>
        <div className="grid grid-cols-7 text-center text-xs text-slate-500 mb-1">
          {DIAS.map((dia, i) => <span key={i}>{dia}</span>)}
        </div>
        {semanas.map((semana, i) => (
          <div key={i} className="grid grid-cols-7 text-center">
            {semana.map((data, j) => {
              if (data === null) return <span key={j} />
              const cor = data === hojeISO && retornoAtrasado
                ? 'vermelha'
                : corDoDia(doMes.filter((c) => c.data === data), agora)
              const eHoje = data === dataISOLocal(agora)
              return (
                <button
                  key={data} type="button" data-testid={`dia-${data}`} onClick={() => setSelecionado(data)}
                  className={`py-1.5 flex flex-col items-center rounded-lg text-sm ${
                    data === selecionado ? 'bg-teal-700 text-white font-bold' : eHoje ? 'text-teal-800 font-bold' : 'text-slate-700'}`}
                >
                  {Number(data.slice(8))}
                  <span className={`mt-0.5 w-1.5 h-1.5 rounded-full ${cor ? BOLINHA[cor] : 'bg-transparent'}`} />
                </button>
              )
            })}
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <p className="font-semibold text-slate-800">
          {DIAS_EXTENSO[new Date(a, m - 1, d).getDay()]} {String(d).padStart(2, '0')}/{String(m).padStart(2, '0')}
        </p>
        {selecionado === hojeISO && retornos.length > 0 && (
          <ul data-testid="retornos-do-dia" className="space-y-2">
            {retornos.map((r) => (
              <li key={r.cliente.id} data-testid={`pendencia-retorno-${r.cliente.id}`}
                className="rounded-xl bg-white shadow-sm p-3 flex items-center gap-2 border-l-4 border-red-500">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 truncate">
                    Retorno: {r.cliente.nome_fantasia || r.cliente.razao_social}
                  </p>
                  <p className="text-xs text-red-600">
                    {r.diasDeAtraso > 0 ? `atrasado há ${r.diasDeAtraso} dia(s)` : 'vence hoje'}
                  </p>
                </div>
                <button type="button" onClick={() => navegar('/agenda')}
                  className="text-xs rounded px-2 py-1 bg-teal-700 text-white">Feito</button>
                <button type="button" onClick={() => void excluirRetorno(r)}
                  className="text-xs rounded px-2 py-1 border border-red-500 text-red-600">Excluir</button>
              </li>
            ))}
          </ul>
        )}
        {lista.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhum compromisso neste dia</p>
        ) : (
          <ul data-testid="lista-do-dia" className="bg-white rounded-xl shadow-sm divide-y divide-slate-100">
            {lista.map((c) => {
              const atrasado = ehAtrasado(c, agora)
              const cliente = nomeDoCliente(clientes, c.cliente_id)
              if (c.apagado_em !== null) return (
                <li key={c.id} data-testid={`excluido-${c.id}`} className="flex items-center gap-3 px-3 py-2 bg-slate-50 text-slate-400">
                  <span className="w-12 shrink-0 font-semibold line-through">{c.hora}</span>
                  <span className="min-w-0 flex-1 text-sm line-through">
                    {c.assunto}
                    {cliente && <span className="block text-xs">{cliente}</span>}
                  </span>
                  <span className="shrink-0 rounded-full px-2 py-0.5 text-xs bg-slate-200 text-slate-500">Excluído</span>
                </li>
              )
              return (
                <li key={c.id}>
                  <Link to={`/compromissos/${c.id}`} data-testid={`compromisso-${c.id}`} className="flex items-center gap-3 px-3 py-2">
                    <span className="w-12 shrink-0 font-semibold text-slate-800">{c.hora}</span>
                    <span className="min-w-0 flex-1 text-sm text-slate-700">
                      {c.assunto}
                      {cliente && <span className="block text-xs text-slate-500">{cliente}</span>}
                    </span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${
                      c.situacao === 'feito' ? 'bg-emerald-100 text-emerald-800' : atrasado ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>
                      {c.situacao === 'feito' ? 'Feito' : 'Pendente'}
                    </span>
                  </Link>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </section>
  )
}
